import React, { useState, useEffect } from "react";
import { Col, Row, Button } from "reactstrap";
import axios from "axios";
import ReactApexChart from "react-apexcharts";
import { constants } from "node:fs/promises";

interface BookedContractedData {
  company_name: string;
  title: string;
  placement_id: number;
  month: number;
  strMonth: string;
  placement_contracted: number | null;
  total_normal_bill: number | null;
}

const BookedContractedReport = () => {
  const [data, setData] = useState<BookedContractedData[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartOptions, setChartOptions] = useState<any>(null);
  const [chartSeries, setChartSeries] = useState<any>([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/booked-contracted");
      const results: BookedContractedData[] = response.data.map((item: any) => {
        const placement_contracted = item.placement_contracted
          ? parseFloat(item.placement_contracted)
          : null;
        let total_normal_bill: number | null = item.total_normal_bill
          ? parseFloat(item.total_normal_bill)
          : null;
        return {
          company_name: item.company_name || "",
          title: item.title || "",
          placement_id: item.placement_id || 0,
          month: item.month,
          strMonth: item.strMonth,
          placement_contracted,
          total_normal_bill,
        };
      });
      setData(results);
      setLoading(false);
    } catch (err: any) {
      console.error("Error fetching data:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Compute totals and update chart options when data is loaded.
  useEffect(() => {
    if (data) {
      const totalBooked = data.reduce(
        (sum, item) => sum + (item.total_normal_bill || 0),
        0
      );
      console.log("Total Booked:", totalBooked);
      const totalContracted = data.reduce(
        (sum, item) => sum + (item.placement_contracted || 0),
        0
      );
      console.log("Total Contracted:", totalContracted);
      const totalOverall = totalContracted;
      // Adjust contracted to show only the difference from booked
      setChartOptions({
        chart: {
          type: "bar",
          height: 350,
          stacked: true,
        },
        plotOptions: {
          bar: {
            distributed: true,
            horizontal: false,
          },
        },
        dataLabels: {
          enabled: true,
          formatter: function (val: number) {
            return "$" + Math.round(val).toLocaleString();
          },
          dropShadow: { enabled: false },
          style: { fontSize: "14px", fontWeight: "400", colors: ["#000000"] },
        },
        xaxis: {
          categories: ["Revenue", "Bookings", "Total"],
        },
        yaxis: {
          labels: {
            formatter: (value: number) => {
              if (value >= 1_000_000) {
                return "$" + (value / 1_000_000).toFixed(1) + "M"; // Converts to 1M, 2.5M, etc.
              } else if (value >= 1_000) {
                return "$" + (value / 1_000).toFixed(1) + "K"; // Converts to 1K, 2.5K, etc.
              }
              return value.toString(); // Keeps smaller values unchanged
            },
          },
        },
      });
      setChartSeries([
        {
          name: "Amount",
          data: [totalBooked, totalOverall - totalBooked, totalOverall],
        },
      ]);
    }
  }, [data]);

  const totalBooked = data
    ? data.reduce((sum, item) => sum + (item.total_normal_bill || 0), 0)
    : 0;
  const totalContracted = data
    ? data.reduce((sum, item) => sum + (item.placement_contracted || 0), 0)
    : 0;
  const totalOverall = totalBooked + totalContracted;

  const handleDownloadCSV = () => {
    if (!data || data.length === 0) return;

    const headers = [
      "Company Name",
      "Title",
      "Placement ID",
      "Month",
      "Month Name",
      "Total Contracted",
      "Booked",
    ];

    const rows = data.map((item) => [
      item.company_name || "",
      item.title || "",
      item.placement_id || "",
      item.month,
      item.strMonth,
      item.placement_contracted ?? "",
      item.total_normal_bill ?? "",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((field) => `"${String(field).replace(/"/g, '""')}"`) // Escape quotes
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "booked_contracted_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return <div>Loading Revenue and Bookings Report...</div>;
  }
  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <Col xs='12'>
      <Row>
        <Col xs='12'>
          <h3>Revenue and Bookings Report</h3>
        </Col>
      </Row>
      <Row>
        <Col xs='12'>
          <h5>Total Summary</h5>
          <p>Revenue: ${totalBooked.toLocaleString()}</p>
          <p>Bookings: ${(totalContracted - totalBooked).toLocaleString()}</p>
          <p>Total: ${totalContracted.toLocaleString()}</p>
        </Col>
      </Row>
      <Row className='mt-3'>
        <Col xs='12'>
          <Button color='primary' onClick={() => handleDownloadCSV()}>
            Download CSV
          </Button>
        </Col>
      </Row>
      <Row>
        <Col xs='12'>
          {chartOptions && chartSeries.length > 0 && (
            <ReactApexChart
              options={chartOptions}
              series={chartSeries}
              type='bar'
              height={350}
            />
          )}
        </Col>
      </Row>
    </Col>
  );
};

export default BookedContractedReport;
